import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/wix-zu-shopify";
const TITLE = "Wix zu Shopify wechseln: Ablauf, Daten, Weiterleitungen";
const DESCRIPTION =
  "Von Wix zu Shopify umziehen: Welche Daten sich exportieren lassen, wie die alten Adressen weitergeleitet werden und was der Wechsel realistisch kostet.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Der Umzug von Wix zu Shopify ist technisch überschaubar: Produkte und Kunden lassen sich als CSV exportieren, Inhalte werden meist neu aufgebaut. Entscheidend sind die Weiterleitungen der alten Wix-Adressen und ein sauber strukturierter Produktkatalog. Kleine Shops sind in zwei bis vier Wochen umgezogen.";

const faqs = [
  {
    q: "Kann ich meine Wix-Produkte zu Shopify übertragen?",
    a: "Ja, Wix erlaubt einen CSV-Export von Produkten und Kundendaten, den Shopify importieren kann. Bilder werden dabei über Adressen verknüpft, prüfen Sie nach dem Import Varianten, Bestände und Bildzuordnung stichprobenartig.",
  },
  {
    q: "Verliere ich meine Google-Platzierungen?",
    a: "Nur wenn Weiterleitungen fehlen. Wix nutzt eigene Adressmuster für Produkt- und Blogseiten, jede davon braucht in Shopify ein 301-Ziel. Mit vollständiger Liste stabilisiert sich die Sichtbarkeit meist innerhalb weniger Wochen.",
  },
  {
    q: "Lohnt sich der Wechsel von Wix überhaupt?",
    a: "Er lohnt sich, wenn der Shop wächst: mehr Produkte, Varianten, Zahlungsarten, Versandregeln und Apps. Für eine kleine Seite mit wenigen Artikeln und geringem Umsatz ist der Aufwand selten gerechtfertigt.",
  },
  {
    q: "Was kostet der Umzug von Wix zu Shopify?",
    a: "Kleine Shops liegen häufig bei 2.500 bis 6.000 € netto inklusive Theme-Aufbau und Weiterleitungen. Reine Datenübernahme ohne neues Theme ist günstiger. Entwicklung berechne ich mit 110 € netto pro Stunde.",
  },
  {
    q: "Wie lange dauert der Wechsel?",
    a: "Zwei bis vier Wochen für einen überschaubaren Katalog, inklusive Theme, Zahlungsarten, Versandregeln, Rechtstexten und Testbestellungen.",
  },
];

export const Route = createFileRoute("/ratgeber/wix-zu-shopify")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Migration",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Migration"
      title="Wix zu Shopify: der Wechsel, wenn der Shop wächst"
      intro="Wix reicht für den Start. Sobald Varianten, Versandregeln, Zahlungsarten und Marketing-Tools dazukommen, wird es eng. Der Umzug zu Shopify ist dann kein Großprojekt, sondern eine Frage sauberer Vorbereitung."
      source="ratgeber-wix-zu-shopify"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Woran Sie merken, dass Wix nicht mehr passt",
          p: [],
          list: [
            "Varianten und Bestände lassen sich nur umständlich pflegen",
            "Versand- und Steuerregeln bilden Ihre Realität nicht ab",
            "Benötigte Tools gibt es nur als Shopify-App",
            "Die mobile Ladezeit bremst und lässt sich kaum beeinflussen",
          ],
        },
        {
          h: "Der Ablauf in vier Schritten",
          p: [],
          list: [
            "Adressliste aus Wix und der Search Console ziehen",
            "Produkte und Kunden als CSV exportieren und bereinigen",
            "Shopify-Theme aufbauen, Produktseite und Kaufabschluss klar strukturieren",
            "301-Weiterleitungen setzen, testen und live gehen",
          ],
        },
        {
          h: "Was Sie neu aufbauen",
          p: [
            "Inhaltsseiten, Blogbeiträge und Navigation wandern in der Regel nicht automatisch mit. Das ist kein Nachteil: Es ist die Gelegenheit, Kategorien und Texte an dem auszurichten, wonach Ihre Kunden tatsächlich suchen.",
          ],
        },
        {
          h: "Kosten und Zeitrahmen",
          p: [
            "Kleine Shops liegen häufig bei 2.500 bis 6.000 € netto und zwei bis vier Wochen. Entwicklung rechne ich mit 110 € netto pro Stunde ab, Conversion-Optimierung ab 3.990 € netto.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-shop-erstellen-lassen", label: "Shopify Shop erstellen lassen" },
        { to: "/ratgeber/shopify-migration", label: "Shopify Migration im Überblick" },
        { to: "/ratgeber/shopify-kosten", label: "Shopify Kosten" },
        { to: "/kontakt", label: "Umzug anfragen" },
      ]}
    />
  );
}
